import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import BrandLogo from "../brand/BrandLogo";
export default function Footer() {
  const { t } = useTranslation(["common"]);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setStatus("error");
      setMessage(t("footer.newsletterInvalid"));
      return;
    }

    setStatus("loading");
    setMessage("");

    // Simulate API call
    setTimeout(() => {
      setStatus("success");
      setMessage(t("footer.newsletterSuccess"));
      setEmail("");

      // Reset success message after 3 seconds
      setTimeout(() => {
        setStatus("idle");
        setMessage("");
      }, 3000);
    }, 1500);
  };

  return (
    <footer className="bg-gradient-to-br from-sage-700 via-sage-600 to-terra-700 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="rounded-xl bg-white px-3 py-2">
                <BrandLogo compact />
              </div>
            </div>
            <p className="text-white/90 mb-6 leading-relaxed text-sm">
              {t("footer.description")}
            </p>
            <div className="flex gap-3">
              <a
                href="#"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-lg"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-line text-lg"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-linkedin-fill text-lg"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base font-bold mb-6">{t("footer.company")}</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/corporate"
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm"
                >
                  {t("footer.about")}
                </Link>
              </li>
              <li>
                <Link
                  to="/how-it-works"
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm"
                >
                  {t("footer.howItWorks")}
                </Link>
              </li>
              <li>
                <Link
                  to="/members-map"
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm"
                >
                  {t("footer.members")}
                </Link>
              </li>
              <li>
                <Link
                  to="/destinations"
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm"
                >
                  {t("footer.destinations")}
                </Link>
              </li>
              <li>
                <Link
                  to="/community"
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm"
                >
                  {t("footer.community")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-base font-bold mb-6">{t("footer.support")}</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/help"
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm"
                >
                  {t("footer.help")}
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm"
                >
                  {t("footer.terms")}
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm"
                >
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to="/careers"
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm"
                >
                  {t("footer.careers")}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-white/80 hover:text-white transition-colors cursor-pointer text-sm"
                >
                  {t("footer.contact")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-base font-bold mb-6">
              {t("footer.newsletter")}
            </h3>
            <p className="text-white/80 mb-4 text-sm">
              {t("footer.newsletterText")}
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col gap-2">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (status === "error") setStatus("idle");
                    setMessage("");
                  }}
                  disabled={status === "loading" || status === "success"}
                  placeholder={t("footer.emailPlaceholder")}
                  className={`flex-1 px-4 py-3 rounded-lg bg-white/10 border text-white placeholder-white/60 focus:outline-none focus:border-white/40 text-sm transition-colors
                    ${status === "error" ? "border-red-400 bg-red-400/10" : "border-white/20"}
                    ${status === "success" ? "border-green-400 bg-green-400/10" : ""}
                  `}
                />
                <button
                  type="submit"
                  disabled={status === "loading" || status === "success"}
                  className={`px-5 py-3 font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center justify-center min-w-[3.5rem]
                    ${
                      status === "success"
                        ? "bg-green-500 text-white hover:bg-green-600"
                        : "bg-white text-terra-600 hover:bg-white/90"
                    }
                  `}
                >
                  {status === "loading" ? (
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                  ) : status === "success" ? (
                    <i className="ri-check-line text-xl"></i>
                  ) : (
                    <i className="ri-send-plane-fill text-xl"></i>
                  )}
                </button>
              </div>
              {message && (
                <p
                  className={`text-xs ${status === "error" ? "text-red-300" : "text-green-300"} animate-fade-in`}
                >
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div>
              <p className="text-sm text-gray-400 mb-1">
                {t(
                  "common:migrated.traveltrok_connecte_les_h_tes_du_monde_entier_pour_des_s_jours_authentiques_et_abordables_",
                )}
              </p>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <span className="font-semibold text-terra-400">
                  {t("common:migrated.____100__made_in_france__picardie_")}
                </span>
                <span className="hidden sm:inline text-gray-500">•</span>
                <span className="text-gray-500 font-medium italic">
                  {t(
                    "common:migrated.pas_la_silicon_valley__mais_la_vall_e_de_la_haute_somme_",
                  )}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-white/80">
              <Link
                to="/terms"
                className="hover:text-white transition-colors cursor-pointer"
              >
                {t("footer.terms")}
              </Link>
              <Link
                to="/privacy"
                className="hover:text-white transition-colors cursor-pointer"
              >
                {t("footer.privacy")}
              </Link>
              <a
                href="https://lepluk.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors cursor-pointer"
              >
                Powered by Plük
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
